import React from 'react';
import { Button } from '@/components/ui/button';
import { Brain, Heart, Users, Calendar } from 'lucide-react';

const GenZPrograms = () => {
  const programs = [
    {
      title: 'Mental Health & Wellbeing',
      description: 'Comprehensive program addressing stress, anxiety, and emotional challenges faced by Gen Z',
      image: 'https://images.unsplash.com/photo-1611569066317-81edf9bee2de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxtZW50YWwlMjBoZWFsdGglMjB3ZWxsYmVpbmd8ZW58MHx8fHwxNzYxMzk1OTI0fDA&ixlib=rb-4.1.0&q=85',
      topics: ['Stress Management', 'Anxiety Coping Strategies', 'Mindfulness Practices', 'Self-Care Routines'],
      icon: Brain
    },
    {
      title: 'Emotional Intelligence',
      description: 'Build strong emotional foundations with practical skills for self-awareness and empathy',
      image: 'https://images.pexels.com/photos/6932294/pexels-photo-6932294.jpeg',
      topics: ['Self-Awareness', 'Emotional Regulation', 'Empathy Building', 'Social Skills'],
      icon: Heart
    }
  ];

  return (
    <div className="genz-programs-page">
      {/* Header */}
      <section className="py-20 bg-gradient-to-br from-[#f5f3f8] via-[#f5f3f8] to-[#f5f3f8]">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-800 mb-6" data-testid="genz-programs-header">Gen Z Programs</h1>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Empowering young adults with essential life skills for mental health, emotional intelligence, and personal growth.
          </p>
        </div>
      </section>

      {/* Programs */}
      <section className="py-20 bg-white" data-testid="programs-list">
        <div className="container mx-auto px-6">
          <div className="space-y-16">
            {programs.map((program, index) => {
              const IconComponent = program.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-white to-[#f5f3f8] rounded-3xl shadow-xl overflow-hidden" data-testid={`program-item-${index}`}>
                  <div className={`grid md:grid-cols-2 gap-8 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                    <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                      <img 
                        src={program.image} 
                        alt={program.title} 
                        className="w-full h-[400px] object-cover"
                      />
                    </div>
                    <div className={`p-8 space-y-6 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#a855f7] to-[#a855f7] rounded-2xl flex items-center justify-center shadow-lg">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        <h2 className="text-3xl lg:text-4xl font-bold text-gray-800">{program.title}</h2>
                      </div>
                      <p className="text-lg text-gray-700 leading-relaxed">{program.description}</p>
                      
                      <div>
                        <h3 className="text-xl font-bold text-gray-800 mb-4">What You'll Learn:</h3>
                        <div className="grid grid-cols-2 gap-3">
                          {program.topics.map((topic, topicIndex) => (
                            <div key={topicIndex} className="flex items-center gap-2 bg-[#f5f3f8] p-3 rounded-lg">
                              <div className="w-2 h-2 bg-[#a855f7] rounded-full" />
                              <span className="text-gray-700 font-medium">{topic}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button 
                        data-testid={`enroll-btn-${index}`}
                        className="w-full md:w-auto bg-[#a855f7] hover:bg-[#9333ea] text-white px-12 py-6 text-lg rounded-full"
                      >
                        Enroll Now
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community & Webinars */}
      <section className="py-20 bg-gradient-to-br from-[#f5f3f8] to-[#f5f3f8]" data-testid="community-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 text-center mb-12">Join Our Community</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-xl text-center space-y-4">
                <Users className="w-16 h-16 text-[#14b8a6] mx-auto" />
                <h3 className="text-2xl font-bold text-gray-800">Community Support</h3>
                <p className="text-gray-600">Connect with peers, share experiences, and grow together in a supportive environment</p>
                <Button 
                  data-testid="join-community-btn"
                  className="bg-[#14b8a6] hover:bg-[#3b82f6] text-white px-8 py-4 rounded-full"
                >
                  Join Community
                </Button>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-xl text-center space-y-4">
                <Calendar className="w-16 h-16 text-[#ff9500] mx-auto" />
                <h3 className="text-2xl font-bold text-gray-800">Live Webinars</h3>
                <p className="text-gray-600">Participate in interactive sessions with experts and learn practical strategies</p>
                <Button 
                  data-testid="view-webinars-btn"
                  className="bg-[#ff9500] hover:bg-[#ff8000] text-white px-8 py-4 rounded-full"
                >
                  View Schedule
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GenZPrograms;